import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useImmer } from "use-immer";
import { produce } from "immer";
import Axios from "axios";
import { flowRight } from "lodash";
import { Checkbox, Container, Content, Table, Text, HStack } from "rsuite";
import { SortType } from "rsuite-table";

import withLoader, { WithLoaderProps } from "hocs/withLoader/withLoader";
import withError, { WithErrorProps } from "hocs/withError/withError";

import useRefreshPage from "hooks/useRefreshPage";
import useRequestCancellation from "hooks/useRequestCancellation";

import constants from "utils/constants";
import { getSorted } from "utils/sort-halper";

import { getErrorMessage } from "api/defaults";
import { loadCompanies } from "api/environments/tenants/companies-api";
import { loadEnrollCache } from "api/environments/tenants/tenant-enrollments-api";

import { Company } from "store/environments/tenants/companies/companies-types";
import {
    EnrollWorkflow,
    EnrollStep,
    StepStatus,
    EnrollCache,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-types";

import { AppDispatch, RootState } from "store/store";
import {
    getActiveStep,
    getEnrollCache,
    getEnrollWorkflow,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-selectors";
import {
    setActiveStepData,
    setEnrollCache,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-actions";
import {
    getActiveEnvironmentId,
    getActiveEnvironmentMoniker,
} from "store/environments/environment-selectors";
import { setIsUnsavedChangeAvailable } from "store/common/unsaved-data/unsaved-data-actions";

import "./Companies.less";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props extends WithErrorProps, WithLoaderProps {}

const Companies: React.FC<Props> = ({ showLoader, showError }: Props) => {
    const { t } = useTranslation();
    const cancelTokenSource = useRequestCancellation();

    const dispatch = useAppDispatch();

    const [dataFetched, setDataFetched] = useState<boolean>(false);

    const [companies, setCompanies] = useImmer<Company[]>([]);

    const [sortColumn, setSortColumn] = useState<keyof Company>();
    const [sortType, setSortType] = useState<SortType>();
    const [allChecked, setAllChecked] = useState<boolean>(false);

    const indeterminate = companies.some((ti) => ti.disabled);

    const activeEnvironmentMoniker = useSelector((state: RootState): string =>
        getActiveEnvironmentMoniker(state)
    );

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const enrollWorkflow = useSelector(
        (state: RootState): EnrollWorkflow | null => getEnrollWorkflow(state)
    );

    const activeStep = useSelector((state: RootState): EnrollStep | null =>
        getActiveStep(state)
    );

    const enrollCache = useSelector((state: RootState): EnrollCache | null =>
        getEnrollCache(state)
    );

    const disabled = activeStep?.status !== StepStatus.Running;

    const headerHeight = 40;
    const rowHeight = 46;

    const fetchData = () => {
        showLoader?.(true);
        showError?.("");

        Promise.all([fetchCompanies()])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    showError?.(message);
                }
            })
            .finally(() => {
                showLoader?.(false);
                setDataFetched(true);
            });
    };

    const fetchCompanies = async (): Promise<string | null> => {
        try {
            switch (activeStep?.status) {
                case StepStatus.Running:
                    const [payload, status] = await loadCompanies(
                        activeEnvironmentId,
                        enrollCache!.tenantId,
                        cancelTokenSource
                    );
                    if (status === constants.statusCode.OK && payload) {
                        setCompanies([
                            ...payload.map((c) => ({ ...c, disabled: true })),
                        ]);
                        dispatch(setActiveStepData(JSON.stringify(payload)));

                        return null;
                    }

                    return getErrorMessage(String(payload), status);
                case StepStatus.Success: {
                    setCompanies(
                        (JSON.parse(activeStep.data ?? "") as Company[]) ?? []
                    );
                }
            }
        } catch (e) {
            if (!Axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchCache = async (): Promise<void> => {
        if (enrollCache) {
            return;
        }

        showLoader?.(true);
        showError?.("");

        try {
            const [payload, status] = await loadEnrollCache(
                activeEnvironmentMoniker,
                enrollWorkflow!.id
            );

            if (status === constants.statusCode.OK && payload) {
                dispatch(setEnrollCache(payload));

                return;
            }

            showError?.(getErrorMessage(String(payload), status));
        } catch (e) {
            if (!Axios.isCancel(e)) {
                showError?.(String(e));

                return;
            }
        }

        showLoader?.(true);
    };

    const handleCheck = (item: Company, checked: boolean) => {
        const items = produce(companies, (draft) => {
            draft.forEach((ti) => {
                if (ti.id === item.id) {
                    ti.disabled = !checked;
                }
            });
        });

        dispatch(setActiveStepData(JSON.stringify(items)));
        dispatch(setIsUnsavedChangeAvailable(true));

        setCompanies(items);
        setAllChecked(items.some((ti) => !ti.disabled));
    };

    const handleCheckAll = (checked: boolean) => {
        const items = produce(companies, (draft) => {
            draft.forEach((ti) => {
                if (ti.remoteId !== constants.datCompany) {
                    ti.disabled = !checked;
                }
            });
        });

        dispatch(setActiveStepData(JSON.stringify(items)));
        dispatch(setIsUnsavedChangeAvailable(true));

        setAllChecked(checked);
        setCompanies(items);
    };

    const handleSortColumn = (
        sortColumn: string | undefined,
        sortType: SortType | undefined
    ) => {
        setSortColumn(sortColumn as keyof Company);
        setSortType(sortType);

        setCompanies((draft) =>
            getSorted(draft, sortColumn as keyof Company, sortType)
        );
    };

    useEffect(() => {
        if (enrollWorkflow?.id) {
            fetchCache().then();
        }
    }, [enrollWorkflow?.id]);

    useRefreshPage(() => {
        if (activeEnvironmentMoniker && enrollCache?.tenantId) {
            fetchData();
        }
    });

    useEffect(() => {
        if (activeEnvironmentId && enrollCache?.tenantId) {
            fetchData();
        }
    }, [activeEnvironmentId, enrollCache?.tenantId]);

    return (
        dataFetched && (
            <Container className={"companies__container"}>
                <Content>
                    <Text>{t("enrollTenant.companies.message")}</Text>
                    <Table
                        style={{ margin: "1rem 0" }}
                        autoHeight={true}
                        data={companies}
                        headerHeight={headerHeight}
                        rowHeight={rowHeight}
                        sortColumn={sortColumn}
                        sortType={sortType}
                        onSortColumn={(dataKey, sortType) =>
                            handleSortColumn(dataKey, sortType)
                        }
                    >
                        <Table.Column width={150} fixed>
                            <Table.HeaderCell>
                                <HStack spacing={10}>
                                    <Checkbox
                                        indeterminate={indeterminate}
                                        checked={allChecked}
                                        disabled={disabled}
                                        readOnly={disabled}
                                        onChange={() => {
                                            handleCheckAll(!allChecked);
                                        }}
                                    />
                                    {t(`enrollTenant.companies.active`)}
                                </HStack>
                            </Table.HeaderCell>
                            <Table.Cell>
                                {(rowData: Company) => (
                                    <Checkbox
                                        readOnly={disabled}
                                        disabled={
                                            disabled ||
                                            rowData.remoteId ===
                                                constants.datCompany
                                        }
                                        checked={!rowData.disabled}
                                        onChange={(_, checked) => {
                                            handleCheck(rowData, checked);
                                        }}
                                    />
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column
                            flexGrow={0.5}
                            minWidth={150}
                            fixed
                            sortable
                        >
                            <Table.HeaderCell>
                                {t(`enrollTenant.companies.remoteId`)}
                            </Table.HeaderCell>
                            <Table.Cell dataKey={"remoteId"}>
                                {(rowData: Company) => (
                                    <Text>{rowData.remoteId}</Text>
                                )}
                            </Table.Cell>
                        </Table.Column>

                        <Table.Column
                            flexGrow={1}
                            minWidth={300}
                            fixed
                            sortable
                        >
                            <Table.HeaderCell>
                                {t(`enrollTenant.companies.name`)}
                            </Table.HeaderCell>
                            <Table.Cell dataKey={"name"}>
                                {(rowData: Company) => (
                                    <Text>{rowData.name}</Text>
                                )}
                            </Table.Cell>
                        </Table.Column>
                    </Table>
                </Content>
            </Container>
        )
    );
};

const Component = flowRight(withLoader, withError)(Companies);

export default Component;
